import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { dedupeRequest } from "../utils/requestDedupe";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Button,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CurrencyPoundIcon from '@mui/icons-material/CurrencyPound';
import CurrencyYenIcon from '@mui/icons-material/CurrencyYen';
import BarChartIcon from '@mui/icons-material/BarChart';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HotelIcon from '@mui/icons-material/Hotel';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import useTokenValidation from "../hooks/useTokenValidation";
import useHotelList from "../hooks/useHotelList";
import useCustomToast from "../hooks/useCustomToast";
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';
import { getHotelCurrencySymbol } from "../utils/currencyUtils";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';
function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBookings: 0,
    totalRevenue: 0,
    last30DaysRevenue: 0,
    last90DaysRevenue: 0,
    allTimeRevenue: 0,
    futureAndPast90DaysRevenue: 0
  });
  const [selectedHotel, setSelectedHotel] = useState(null);
  const hotelIdRef = useRef(localStorage.getItem("selectedHotelId") || "");
  const statsFetchedForRef = useRef(null);
  const navigate = useNavigate();
  const { successToast, errorToast } = useCustomToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { hotels, loading: hotelsLoading } = useHotelList();


  // Animation variants
  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemAnimation = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  // Check initial load for selectedHotel in localStorage
  useEffect(() => {
    const storedHotelId = localStorage.getItem("selectedHotelId");
    const storedHotelName = localStorage.getItem("selectedHotelName");
    
    if (storedHotelId && storedHotelName) {
      setSelectedHotel({
        hotel_id: storedHotelId,
        hotel_name: storedHotelName
      });
      
      const hostId = localStorage.getItem("selectedHostId");
      if (hostId) {
        const key = `${hostId}:${storedHotelId}`;
        statsFetchedForRef.current = key;
        fetchBookingStats(hostId, storedHotelId);
      }
    }
  }, []);

  // Sync selected hotel object when hotels list arrives — don't re-fetch stats if already loaded for this hotel
  useEffect(() => {
    if (hotels && hotels.length > 0) {
      const storedHotelId = localStorage.getItem("selectedHotelId");
      if (storedHotelId) {
        const foundHotel = hotels.find(h => String(h.hotel_id) === String(storedHotelId));
        if (foundHotel) {
          setSelectedHotel(foundHotel);
          if (foundHotel.host_id) {
            localStorage.setItem("selectedHostId", foundHotel.host_id);
            const already = statsFetchedForRef.current;
            const key = `${foundHotel.host_id}:${storedHotelId}`;
            if (already !== key) {
              statsFetchedForRef.current = key;
              fetchBookingStats(foundHotel.host_id, storedHotelId);
            }
          }
        }
      }
    }
  }, [hotels]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "selectedHotelId") {
        const newHotelId = localStorage.getItem("selectedHotelId") || '';
        if (newHotelId !== hotelIdRef.current) {
          hotelIdRef.current = newHotelId;
          window.location.reload();
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const fetchHotelDetails = async () => {
    try {
      const token = getDecryptedToken();
      if (!token) {
        errorToast("Please login to continue");
        navigate("/login");
        return;
      }

        const formData = new FormData();
        formData.append("token", token);

        const response = await axios.post(
        "https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHostAllHotels",
          formData
        );

      if (response.data.status) {
        const hotelsData = response.data.data;
        
        // If we have a selected hotel ID but not the full object, find it in the hotels array
        const storedHotelId = localStorage.getItem("selectedHotelId");
        const storedHotelName = localStorage.getItem("selectedHotelName");
        if (!storedHotelId || !storedHotelName) {
          const hotelsListStr = localStorage.getItem('hotelsList');
          let hotelsList = null;
          try {
            const SECRET_KEY = 'tabtrips_hotel_secret';
            const bytes = CryptoJS.AES.decrypt(hotelsListStr, SECRET_KEY);
            const decrypted = bytes.toString(CryptoJS.enc.Utf8);
            hotelsList = JSON.parse(decrypted);
          } catch (e) {
            hotelsList = null;
          }
          if (hotelsList && hotelsList.length > 0) {
            const firstHotel = hotelsList[0];
          localStorage.setItem("selectedHotelId", firstHotel.hotel_id);
          localStorage.setItem("selectedHotelName", firstHotel.hotel_name);
          localStorage.setItem("selectedHostId", firstHotel.host_id);
          setSelectedHotel(firstHotel);
          if (firstHotel.host_id && firstHotel.hotel_id) {
            fetchBookingStats(firstHotel.host_id, firstHotel.hotel_id);
            }
          }
        }
      } else {
        errorToast(response.data.message || "Failed to fetch hotel details");
      }
      
      return response.data;
      } catch (error) {
        console.error("Error fetching hotel details:", error);
      errorToast("Failed to fetch hotel details");
      } finally {
        setLoading(false);
      }
    };

  // Fetch booking data for the selected hotel
  const fetchBookingStats = async (hostId, hotelId) => {
    try {
      setLoading(true); // Set loading state while fetching bookings
      const token = getDecryptedToken();
      
      const response = await dedupeRequest(
        `getHostAllBookings:${hostId}:${hotelId}`,
        () => {
          const formData = new FormData();
          formData.append("host_id", hostId);
          formData.append("hotel_id", hotelId);
          formData.append("token", token);
          return axios.post(
            "https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHostAllBookings",
            formData
          );
        },
        4000
      );
      
      if (response.data.status && response.data.data) {
        calculateBookingStats(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching booking data:", error);
      // Silently fail with booking stats - don't show toast for this
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate booking statistics from the API data
  const calculateBookingStats = (bookingsData) => {
    if (!bookingsData || !Array.isArray(bookingsData) || bookingsData.length === 0) {
      setStats({
        totalBookings: 0,
        totalRevenue: 0,
        last30DaysRevenue: 0,
        last90DaysRevenue: 0,
        allTimeRevenue: 0,
        futureAndPast90DaysRevenue: 0
      });
      return;
    }
    
    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    
    // Filter bookings for today and future dates
    const currentAndFutureBookings = bookingsData.filter(booking => 
      booking.check_in_date >= today
    );
    
    // Calculate total revenue from all current and future bookings
    const totalRevenue = currentAndFutureBookings.reduce((total, booking) => {
      // Use discounted_amount as the base amount
      const baseAmount = parseFloat(booking.discounted_amount) || 0;
      const gstAmount = parseFloat(booking.gst_amount) || 0;
      const grandTotal = baseAmount + gstAmount;
      
      return total + grandTotal;
    }, 0);
    
    // Calculate all-time revenue from all bookings
    const allTimeRevenue = bookingsData.reduce((total, booking) => {
      const baseAmount = parseFloat(booking.discounted_amount) || 0;
      const gstAmount = parseFloat(booking.gst_amount) || 0;
      const grandTotal = baseAmount + gstAmount;
      
      return total + grandTotal;
    }, 0);
    
    // Calculate last 30 days revenue
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split('T')[0];
    
    const last30DaysBookings = bookingsData.filter(booking => {
      const checkInDate = new Date(booking.check_in_date);
      const checkOutDate = new Date(booking.check_out_date);
      const thirtyDaysAgoDate = new Date(thirtyDaysAgoStr);
      
      // Include bookings that overlap with the last 30 days
      return (checkInDate <= thirtyDaysAgoDate && checkOutDate > thirtyDaysAgoDate) ||
             (checkInDate >= thirtyDaysAgoDate && checkInDate <= new Date());
    });
    
    const last30DaysRevenue = last30DaysBookings.reduce((total, booking) => {
      const baseAmount = parseFloat(booking.discounted_amount) || 0;
      const gstAmount = parseFloat(booking.gst_amount) || 0;
      const grandTotal = baseAmount + gstAmount;
      return total + grandTotal;
    }, 0);
    
    // Calculate last 90 days revenue
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    const ninetyDaysAgoStr = ninetyDaysAgo.toISOString().split('T')[0];
    
    const last90DaysBookings = bookingsData.filter(booking => {
      const checkInDate = new Date(booking.check_in_date);
      const checkOutDate = new Date(booking.check_out_date);
      const ninetyDaysAgoDate = new Date(ninetyDaysAgoStr);
      
      // Include bookings that overlap with the last 90 days
      return (checkInDate <= ninetyDaysAgoDate && checkOutDate > ninetyDaysAgoDate) ||
             (checkInDate >= ninetyDaysAgoDate && checkInDate <= new Date());
    });
    
    const last90DaysRevenue = last90DaysBookings.reduce((total, booking) => {
      const baseAmount = parseFloat(booking.discounted_amount) || 0;
      const gstAmount = parseFloat(booking.gst_amount) || 0;
      const grandTotal = baseAmount + gstAmount;
      return total + grandTotal;
    }, 0);
    
    // Calculate future + past 90 days revenue
    const futureAndPast90DaysRevenue = totalRevenue + last90DaysRevenue;
    
    setStats({
      totalBookings: currentAndFutureBookings.length,
      totalRevenue: totalRevenue,
      last30DaysRevenue: last30DaysRevenue,
      last90DaysRevenue: last90DaysRevenue,
      allTimeRevenue: allTimeRevenue,
      futureAndPast90DaysRevenue: futureAndPast90DaysRevenue
    });
  };

  // Use our custom hook for token validation
  const { handleApiError } = useTokenValidation(fetchHotelDetails, []);

  // Handle hotel card click - now just selects the hotel without navigating
  const handlePropertySelect = async (hotel) => {
    setSelectedHotel(hotel);
    localStorage.setItem("selectedHotelId", hotel.hotel_id);
    localStorage.setItem("selectedHotelName", hotel.hotel_name);
    if (hotel.host_id) {
      localStorage.setItem("selectedHostId", hotel.host_id);
      await fetchBookingStats(hotel.host_id, hotel.hotel_id);
    }
    successToast(`${hotel.hotel_name} selected for management`);
    window.dispatchEvent(new Event('storage'));
    navigate(`/room-rates-calendar/${hotel.hotel_id}`);
  };
  
  // Navigate to room rates calendar for the selected hotel
  const handleManageRates = (hotelId) => {
    navigate(`/room-rates-calendar/${hotelId}`);
  };
  
  // Navigate to reservations page
  const navigateToReservations = () => {
    navigate('/reservations');
  };

  // Determine if stats should be shown
  const shouldShowStats = selectedHotel || hotels.length === 1;

  // Get currency symbol based on selected hotel's address - updates when hotel changes
  const currencySymbol = useMemo(() => {
    // Wait for hotels to load
    if (hotelsLoading || !hotels || hotels.length === 0) {
      return '₹'; // Default to INR while loading
    }
    
    // Get the selected hotel ID from localStorage
    const selectedHotelId = localStorage.getItem("selectedHotelId");
    
    // Always try to find the hotel from the hotels array first (it has the full data with address)
    let hotelWithAddress = null;
    
    if (selectedHotelId) {
      // Find the selected hotel in the hotels array
      hotelWithAddress = hotels.find(h => 
        String(h.hotel_id) === String(selectedHotelId) ||
        h.hotel_id?.toString() === selectedHotelId?.toString()
      );
    }
    
    // If not found or no selection, use first hotel
    if (!hotelWithAddress && hotels.length > 0) {
      hotelWithAddress = hotels[0];
    }
    
    // Fallback to selectedHotel if hotels array doesn't have it
    if (!hotelWithAddress && selectedHotel) {
      hotelWithAddress = selectedHotel;
    }
    
    // Now get the currency symbol
    if (hotelWithAddress) {
      // Check if hotel has address, if not try to get it from hotels array
      if (!hotelWithAddress.address && hotels && hotels.length > 0) {
        const fullHotel = hotels.find(h => 
          String(h.hotel_id) === String(hotelWithAddress.hotel_id) ||
          h.hotel_id?.toString() === hotelWithAddress.hotel_id?.toString()
        );
        if (fullHotel && fullHotel.address) {
          hotelWithAddress = fullHotel;
        }
      }
      
      if (hotelWithAddress.address) {
        const symbol = getHotelCurrencySymbol(hotelWithAddress);
        // Ensure we always return a valid symbol, default to INR if undefined/null or empty
        if (symbol && symbol.trim() !== '') {
          return symbol;
        }
      }
    }
    
    // Default to Indian Rupee if country cannot be determined
    return '₹';
  }, [selectedHotel, hotels, hotelsLoading]);

  // Get currency icon based on currency symbol - returns the symbol as text icon
  const getCurrencyIcon = (symbol) => {
    if (!symbol) {
      return <Box component="span" sx={{ fontSize: 'inherit', fontWeight: 600 }}>₹</Box>;
    }
    
    const symbolUpper = symbol.toUpperCase();
    
    // For rupee currencies, use rupee icon
    if (symbol === '₹' || symbolUpper.includes('RUPEE') || symbolUpper.includes('₨')) {
      return <CurrencyRupeeIcon />;
    }
    
    // For pound, use pound icon
    if (symbol === '£' || symbolUpper.includes('POUND')) {
      return <CurrencyPoundIcon />;
    }
    
    // For yen/yuan, use yen icon
    if (symbol === '¥' || symbolUpper.includes('YEN') || symbolUpper.includes('YUAN')) {
      return <CurrencyYenIcon />;
    }
    
    // For all other currencies (SAR, $, €, etc.), display the symbol itself as text
    return (
      <Box 
        component="span" 
        sx={{ 
          fontSize: 'inherit', 
          fontWeight: 700,
          fontFamily: 'inherit',
          lineHeight: 1
        }}
      >
        {symbol.length <= 4 ? symbol : symbol.substring(0, 4)}
      </Box>
    );
  };

  const currencyIcon = useMemo(() => getCurrencyIcon(currencySymbol), [currencySymbol]);

  // Occupancy trend data for previous year
  const occupancyTrendData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Occupancy Rate (%)',
        data: [65, 72, 78, 85, 92, 88, 95, 91, 87, 82, 76, 70],
        backgroundColor: 'rgba(76, 175, 80, 0.8)',
        borderColor: '#4CAF50',
        borderWidth: 3,
        borderRadius: 8,
        borderSkipped: false,
      }
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'top',
        labels: {
          boxWidth: isMobile ? 8 : 10,
          usePointStyle: true,
          pointStyle: 'circle',
          padding: isMobile ? 10 : 20,
          color: '#2E4057',
          font: {
            size: isMobile ? 10 : 12
          }
        },
      },
      tooltip: {
        backgroundColor: 'rgba(46, 64, 87, 0.9)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: isMobile ? 8 : 12,
        titleFont: {
          size: isMobile ? 12 : 14
        },
        bodyFont: {
          size: isMobile ? 11 : 13
        },
        usePointStyle: true,
        boxWidth: isMobile ? 8 : 10,
        callbacks: {
          label: function(context) {
            return `Occupancy: ${context.parsed.y}%`;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: 'rgba(0, 0, 0, 0.04)',
          drawBorder: false,
        },
        ticks: {
          callback: (value) => `${value}%`,
          color: '#2E4057',
          font: {
            size: isMobile ? 10 : 12
          },
          maxTicksLimit: isMobile ? 6 : 8,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: '#2E4057',
          font: {
            size: isMobile ? 10 : 12
          },
          maxRotation: isMobile ? 45 : 0,
          minRotation: isMobile ? 45 : 0,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    animation: {
      duration: 1000,
    },
    barThickness: isMobile ? 35 : 45,
    maxBarThickness: isMobile ? 40 : 50,
    categoryPercentage: 0.8, // Controls the width of the entire category (bar group)
    barPercentage: 0.9, // Controls the width of individual bars within the category
  };



  return (
    <div className="dashboard-container">
      <div className="dashboard">
        <Box
          component={motion.div}
          variants={containerAnimation}
          initial="hidden"
          animate="visible"
          sx={{
            width: '100%',
            maxWidth: '100%',
            margin: '0 auto',
            p: { xs: 0, sm: 1 },
          }}
        >
        {/* Dashboard Header */}
          <motion.div variants={itemAnimation}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: '#2E4057',
                mb: 3,
                fontSize: { xs: '1.5rem', sm: '2rem' },
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: 0,
                  width: '60px',
                  height: '4px',
                  background: 'linear-gradient(90deg, #FF7700, transparent)',
                  borderRadius: '2px',
                }
              }}
            >
              Dashboard
            </Typography>
          </motion.div>

          {/* Stats Overview */}
          {shouldShowStats ? (
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }} sx={{ mb: 4 }}>
              {[
                {
                  title: "Properties",
                  value: hotels.length,
                  icon: <HomeWorkIcon />,
                  color: '#FF7700',
                  onClick: () => navigate('/')
                },
                {
                  title: "Active Bookings",
                  value: stats.totalBookings,
                  icon: <EventAvailableIcon />,
                  color: '#4CAF50',
                  subtitle: "Current & future",
                  onClick: navigateToReservations
                },
                {
                  title: "Total Revenue",
                  value: `${currencySymbol} ${stats.futureAndPast90DaysRevenue.toLocaleString()}`,
                  icon: currencyIcon,
                  color: '#9C27B0',
                  subtitle: "Future + Past 90 days revenue",
                  onClick: navigateToReservations
                },
                {
                  title: "Future Revenue",
                  value: `${currencySymbol} ${stats.totalRevenue.toLocaleString()}`,
                  icon: currencyIcon,
                  color: '#2196F3',
                  subtitle: "Current & future",
                  onClick: navigateToReservations
                },
                {
                  title: "Last 30 Days Revenue",
                  value: `${currencySymbol} ${stats.last30DaysRevenue.toLocaleString()}`,
                  icon: <TrendingUpIcon />,
                  color: '#4CAF50',
                  
                  additionalInfo: `Past 90 days revenue: ${currencySymbol} ${stats.last90DaysRevenue.toLocaleString()}`,
                  onClick: () => null
                }
              ].map((stat, index) => (
                <Grid item xs={12} sm={6} md={2.4} key={index}>
                  <motion.div variants={itemAnimation}>
                    <Card
                      onClick={stat.onClick}
                      sx={{
                        height: { xs: '140px', sm: '160px', md: '180px' },
                        cursor: 'pointer',
                        background: '#fff',
                        borderRadius: { xs: '12px', sm: '16px' },
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease',
                        overflow: 'hidden',
                        position: 'relative',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                          '& .stat-icon': {
                            transform: 'scale(1.1)',
                          }
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '4px',
                          background: stat.color,
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        p: { xs: 1.5, sm: 2, md: 2.5 },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        '&:last-child': { pb: { xs: 1.5, sm: 2, md: 2.5 } }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flex: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 600,
                                color: '#2E4057',
                                mb: 0.5,
                                fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                                lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 }
                              }}
                            >
                              {stat.value}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                                fontWeight: 500,
                                lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 }
                              }}
                            >
                              {stat.title}
                            </Typography>
                            {stat.subtitle && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: stat.title === "Total Revenue" ? '#9C27B0' : 'text.disabled',
                                  display: 'block',
                                  mt: 0.5,
                                  fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                                  fontWeight: stat.title === "Total Revenue" ? 600 : 400,
                                  opacity: stat.title === "Total Revenue" ? 1 : 0.7
                                }}
                              >
                                {stat.subtitle}
                              </Typography>
                            )}
                            {stat.additionalInfo && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#FF7700',
                                  display: 'block',
                                  mt: 0.5,
                                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                                  fontWeight: 600,
                                  opacity: 1,
                                  backgroundColor: 'rgba(255, 119, 0, 0.1)',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  border: '1px solid rgba(255, 119, 0, 0.2)'
                                }}
                              >
                                {stat.additionalInfo}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            className="stat-icon"
                            sx={{
                              backgroundColor: `${stat.color}15`,
                              borderRadius: { xs: '8px', sm: '10px', md: '12px' },
                              p: { xs: 1, sm: 1.25, md: 1.5 },
                              color: stat.color,
                              transition: 'transform 0.3s ease',
                              flexShrink: 0,
                              ml: { xs: 1, sm: 1.5, md: 2 }
                            }}
                          >
                            <Box sx={{ 
                              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
                            }}>
                            {stat.icon}
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          ) : (
            <motion.div variants={itemAnimation}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  background: 'linear-gradient(135deg, rgba(255,119,0,0.05) 0%, rgba(255,157,77,0.05) 100%)',
                }}
              >
                <Typography variant="h6" gutterBottom color="primary">
                  Welcome to Your Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Please select a property from My Properties to view statistics and manage your bookings.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/')}
                  startIcon={<HomeWorkIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                    color: '#fff',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF7700 0%, #FF9D4D 100%)',
                      boxShadow: '0 4px 15px rgba(255, 119, 0, 0.3)',
                    },
                  }}
                >
                  Go to My Properties
                </Button>
              </Card>
            </motion.div>
          )}

          {/* Occupancy Trend Section */}
          {shouldShowStats && (
            <motion.div variants={itemAnimation}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, sm: 3 },
                  mb: 4,
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#2E4057',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}
                  >
                    <TrendingUpIcon sx={{ 
                      color: '#FF7700',
                      fontSize: { xs: '1.25rem', sm: '1.5rem' }
                    }} />
                    Occupancy Trend
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <TrendingUpIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, color: '#4CAF50' }} />
                    Previous Year Occupancy Rates
                  </Typography>
                </Box>
                <Box sx={{ 
                  height: { xs: '250px', sm: '300px', md: '400px' }, 
                  width: '100%',
                  mt: { xs: 1, sm: 2 }
                }}>
                  <Bar 
                    data={occupancyTrendData} 
                    options={chartOptions} 
                  />
                </Box>
              </Paper>
            </motion.div>
          )}
        </Box>
      </div>
    </div>
  );
};

export default Dashboard;